import React, { useEffect, useState } from 'react';
import { Divider, Flex, Select, Tooltip, Typography, Checkbox } from 'antd';
import { DeleteIcon } from '@/assets/icons/icons';
import { CustomShipmentText, CustomTitle } from '../../../../components';
import { capitalize, sentenceCase } from '../../../../common/helper';
import ShipmentAlertThresholdInput from './ShipmentAlertThresholdInput';
import type {
  AlertThreshold,
  SelectedThresholdValues,
} from './AlertFieldByType';

const { Text } = Typography;

interface Shipment {
  id?: string;
  sensor?: unknown;
}

interface ShipmentAlertThresholdsProps {
  step: number;
  shipment: Shipment;
  selectAllAlertTypes: boolean;
  setSelectAllAlertTypes: (value: boolean) => void;
  alertThreshold: AlertThreshold[];
  setSelectedThresholdValues: (
    value:
      | SelectedThresholdValues
      | ((prev: SelectedThresholdValues) => SelectedThresholdValues)
  ) => void;
  selectedThresholdValues: SelectedThresholdValues;
  maxHeight: string;
  showThresholdInputSection?: boolean;
  enableAllThresholds?: boolean;
  setUpdateAlertData?: (value: boolean) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

/**
 * Displays shipment alert thresholds in different step views
 *
 * Step 2: Shows selected thresholds with delete functionality
 * Step 3: Shows all available thresholds with selection interface and "Select all" option
 *
 * @param step - Current step (2 for review, 3 for selection)
 * @param shipment - Shipment data including sensor information
 * @param selectAllAlertTypes - Whether all alert types are selected
 * @param setSelectAllAlertTypes - Callback to toggle select all state
 * @param alertThreshold - Array of available alert threshold configurations grouped by phase
 * @param setSelectedThresholdValues - Callback to update selected threshold values
 * @param selectedThresholdValues - Currently selected threshold values mapped by alert_type
 * @param maxHeight - Maximum height for scrollable threshold list
 * @param showThresholdInputSection - Whether to show threshold input section (default: true)
 * @param enableAllThresholds - Whether to enable all thresholds regardless of sensor requirements (default: false)
 */
const ShipmentAlertThresholds: React.FC<ShipmentAlertThresholdsProps> = ({
  step,
  shipment,
  selectAllAlertTypes,
  setSelectAllAlertTypes,
  alertThreshold,
  setSelectedThresholdValues,
  selectedThresholdValues,
  maxHeight,
  showThresholdInputSection = true,
  enableAllThresholds = false,
  setUpdateAlertData,
  isLoading = false,
  disabled = false,
}) => {
  const [patchedConfig, setPatchedConfig] = useState<
    AlertThreshold[] | undefined
  >();

  useEffect(() => {
    if (alertThreshold?.length > 0) {
      setPatchedConfig(alertThreshold);
    }
  }, [alertThreshold]);

  const handleDelete = (id: string): void => {
    const newValues = { ...selectedThresholdValues };
    delete newValues[id];
    const totalCount = (patchedConfig ?? [])?.flatMap(
      (group) => group?.alerts
    )?.length;
    const selectedCount = Object.keys(newValues)?.length;
    setSelectAllAlertTypes(totalCount > 0 && selectedCount === totalCount);
    setSelectedThresholdValues(newValues);
    if (setUpdateAlertData) {
      setUpdateAlertData(true);
    }
  };

  const onSelectALLChange = (val: boolean): void => {
    let values: SelectedThresholdValues = {};
    setSelectAllAlertTypes(val);
    if (val === true) {
      patchedConfig?.forEach((element) => {
        element?.alerts.forEach((ele) => {
          const disable =
            ele.sensor_required === 'required' &&
            !shipment?.sensor &&
            !enableAllThresholds;
          if (!disable) {
            values = {
              ...values,
              [ele.alert_type]: ele.threshold_value,
            };
          }
        });
      });
    }
    setSelectedThresholdValues(values);
  };

  return (
    <>
      {step === 2 && (
        <div className='alert-thresholds-summary'>
          {patchedConfig?.map((group) =>
            group?.alerts
              .filter((rule) => rule.alert_type in selectedThresholdValues)
              .map((rule) => {
                return (
                  <React.Fragment key={rule.alert_type}>
                    <Flex
                      align='center'
                      justify='space-between'
                      style={{ marginBottom: 12, gap: 16 }}
                    >
                      <Flex vertical style={{ flex: 1 }}>
                        <Text
                          style={{
                            fontSize: 12,
                            fontWeight: 400,
                            color: 'var(--text-primary)',
                            textUnderlineOffset: 2,
                            paddingLeft: 4,
                          }}
                        >
                          {sentenceCase(rule?.alert_name)}
                        </Text>
                      </Flex>

                      <Flex align='center' gap={12}>
                        {rule?.threshold_comparison &&
                          showThresholdInputSection && (
                            <Text
                              style={{
                                fontSize: 12,
                                color: 'var(--text-secondary)',
                                minWidth: 40,
                                textAlign: 'right',
                              }}
                            >
                              {capitalize(rule?.threshold_comparison)}
                            </Text>
                          )}
                        {showThresholdInputSection && (
                          <Flex
                            align='center'
                            justify='space-between'
                            style={{
                              background: 'var(--light-gray)',
                              borderRadius: '8px',
                              padding: '6px 12px',
                              width: 112,
                              height: 36,
                            }}
                          >
                            <Select
                              className='inputThreshold'
                              disabled={true}
                              value={rule.threshold_value}
                              style={{
                                width: 50,
                                backgroundColor: 'transparent',
                                border: 'none',
                                textAlign: 'left',
                              }}
                              suffixIcon={null}
                              dropdownStyle={{ minWidth: 80 }}
                            />
                            <Text
                              style={{
                                fontWeight: 600,
                                fontSize: 12,
                                color: 'var(--text-primary)',
                                marginLeft: 4,
                              }}
                            >
                              {rule.unit}
                            </Text>
                          </Flex>
                        )}
                        {!disabled && (
                          <Tooltip title='Remove'>
                            <DeleteIcon
                              role='img'
                              aria-label='delete'
                              style={{
                                cursor: 'pointer',
                                marginRight: 10,
                              }}
                              onClick={() => handleDelete(rule.alert_type)}
                            />
                          </Tooltip>
                        )}
                      </Flex>
                    </Flex>
                    <Divider
                      style={{
                        borderColor: 'var(--gray-300)',
                        margin: '12px 0',
                      }}
                    />
                  </React.Fragment>
                );
              })
          )}
        </div>
      )}
      {step === 3 && (
        <div
          className='alert-thresholds-list'
          // eslint-disable-next-line react/forbid-dom-props -- maxHeight is a runtime value from the caller
          style={{ maxHeight: maxHeight || '500px' }}
        >
          {/* Show loading state */}
          {isLoading && (
            <Flex
              align='center'
              justify='center'
              style={{
                height: '200px',
              }}
            >
              <CustomShipmentText
                text={'Loading alert types...'}
                fontSize='14px'
                fontWeight={600}
                color='var(--text-secondary)'
              />
            </Flex>
          )}

          {!isLoading && (!patchedConfig || patchedConfig?.length === 0) && (
            <Flex
              align='center'
              justify='center'
              style={{
                height: '200px',
              }}
            >
              <CustomShipmentText
                text={'No data'}
                fontSize='14px'
                fontWeight={600}
                color='var(--text-secondary)'
              />
            </Flex>
          )}

          {/* Show content when not loading and data exists */}
          {!isLoading && patchedConfig && patchedConfig.length > 0 && (
            <>
              <Flex
                align='center'
                justify='flex-start'
                style={{
                  gap: 24,
                  margin: '10px 8px',
                  backgroundColor: 'transparent',
                  borderRadius: 8,
                }}
              >
                <Checkbox
                  checked={selectAllAlertTypes}
                  onChange={(e) => onSelectALLChange(e.target.checked)}
                  disabled={disabled}
                  style={{ margin: 0 }}
                />
                <Flex
                  vertical
                  style={{ paddingRight: 20, flex: 1, minWidth: 0 }}
                >
                  <Flex align='center' gap={4}>
                    <Text
                      style={{
                        fontSize: 14,
                        lineHeight: '20px',
                        fontWeight: '600',
                      }}
                    >
                      {'Select all'}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
              <Divider
                style={{ margin: '16px 0px', borderColor: 'var(--gray-300)' }}
              />
              {patchedConfig?.map((group, index) => (
                <React.Fragment key={group?.phase_display}>
                  {index !== 0 && (
                    <Divider
                      style={{
                        borderColor: 'var(--gray-300)',
                        margin: '0px',
                      }}
                    />
                  )}

                  <CustomTitle
                    text={group?.phase_display}
                    fontSize={14}
                    fontWeight='600'
                    color='var(--text-primary)'
                    marginTop={15}
                    marginBottom='0px'
                  />

                  <Flex align='flex-start'>
                    <Flex vertical style={{ flex: 1 }}>
                      {group.alerts.map((rule) => {
                        const isSelected =
                          rule.alert_type in selectedThresholdValues;
                        return (
                          <div
                            key={rule?.alert_type}
                            className={`alert-threshold-row${isSelected ? ' selected' : ''}`}
                          >
                            <ShipmentAlertThresholdInput
                              shipment={shipment}
                              rule={rule}
                              isChecked={isSelected}
                              enableAllThresholds={enableAllThresholds}
                              disabled={disabled}
                              onCheckboxChange={(checked) => {
                                const newValues = {
                                  ...selectedThresholdValues,
                                };
                                if (checked) {
                                  newValues[rule.alert_type] =
                                    rule.threshold_value;
                                } else {
                                  delete newValues[rule.alert_type];
                                }
                                const totalCount = (
                                  patchedConfig ?? []
                                )?.flatMap((group) => group?.alerts)?.length;
                                const selectedCount =
                                  Object.keys(newValues).length;
                                setSelectAllAlertTypes(
                                  totalCount > 0 && selectedCount === totalCount
                                );
                                setSelectedThresholdValues(newValues);
                              }}
                            />
                          </div>
                        );
                      })}
                    </Flex>
                  </Flex>
                </React.Fragment>
              ))}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ShipmentAlertThresholds;
